import { AfterViewInit, Component, inject, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { TranslocoLocaleService } from '@ngneat/transloco-locale';
import { DataTableColDef } from '@tcc-mono/shared/ui/data-table';
import { CalcAgePipe, withLoading } from '@tcc-mono/shared/utils';
import { Animal, AnimalSelection, animalSelectionIsLocked, getSelectionMarkColorHex, SelectionMarkColor, getMarkColorFilterTitle, filterAnimalsByMarkColor,  } from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { BehaviorSubject, Observable, catchError, combineLatest, filter, firstValueFrom, from, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import {
  AnimalSelectionAnimalDialogComponent,
  AnimalSelectionPoolContext
} from '@tcc-mono/vandijk/shared/features/animal-selections/feature/dialogs/animal-selection-animal-dialog';
import { AnimalSelectionAddAnimal } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { AnimalSelectionAnimalDetailsDialogComponent } from '@tcc-mono/vandijk/shared/features/animal-selections/feature/dialogs/animal-selection-animal-details-dialog';
import { ANIMAL_SELECTION_CONFIG, AnimalSelectionConfig } from '../../../animal-selection-shell/src';
import { LoaderService, ToastService } from '@tcc-mono/shared/ui/services';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'tcc-mono-animal-selection-animals',
  templateUrl: './animal-selection-animals.component.html',
  styleUrls: ['./animal-selection-animals.component.scss']
})
export class AnimalSelectionAnimalsComponent implements OnInit, AfterViewInit {
  private readonly _repo: AnimalSelectionRepository = inject(AnimalSelectionRepository);
  private readonly _locale: TranslocoLocaleService = inject(TranslocoLocaleService);
  private readonly _agePipe: CalcAgePipe = inject(CalcAgePipe);
  private readonly _modalCtrl: ModalController = inject(ModalController);
  private readonly _renderer: Renderer2 = inject(Renderer2);
  private readonly _toast: ToastService = inject(ToastService);
  private readonly _loader: LoaderService = inject(LoaderService);
  private readonly _config: AnimalSelectionConfig = inject(ANIMAL_SELECTION_CONFIG);
  private readonly _platform: Platform = inject(Platform);

  public readonly selection$: Observable<AnimalSelection> = this._repo.activeEntity$;

  /** Filterkleuren met min/max voor de actieve selectie (per-selectie instellingen). */
  public selectionMarkColors$: Observable<SelectionMarkColor[]> = this.selection$.pipe(
    switchMap((selection) =>
      selection ? this._repo.getSelectionMarkColorsForSelection(selection.id) : of([])
    )
  );

  /** Kleuren alleen wanneer er gebruikte kleuren zijn (voor tonen filter-toolbar). */
  public toolbarColors$: Observable<SelectionMarkColor[] | null> = this.selectionMarkColors$.pipe(
    map((colors) => (this.getUsedColors(colors).length > 0 ? colors : null))
  );

  /** Toolbar-data: kleuren + selectie (voor aantallen in filter-dots). */
  public toolbarData$: Observable<{ colors: SelectionMarkColor[] | null; selection: AnimalSelection | null }> =
    combineLatest([this.toolbarColors$, this.selection$]).pipe(
      map(([colors, selection]) => ({ colors, selection }))
    );

  readonly _filterByMarkColorValue$ = new BehaviorSubject<string | null>(null);

  private _rawAnimals$: Observable<Animal[]> = this.selection$.pipe(
    filter((selection) => Array.isArray(selection?.animals)),
    map((selection) => this._repo.getSortedSelectionAnimals(selection!.animals, selection!.id))
  );

  public animals$: Observable<Animal[]> = combineLatest([
    this._rawAnimals$,
    this._filterByMarkColorValue$,
    this.selectionMarkColors$
  ]).pipe(
    map(([animals, colorValue, colors]) => {
      if (colorValue == null) return animals;
      return filterAnimalsByMarkColor(animals, colorValue, colors || []);
    })
  );

  public columns: DataTableColDef[];

  @ViewChild('genderTmpl') public genderTmpl: TemplateRef<any>;
  @ViewChild('colorTmpl') public colorTmpl: TemplateRef<any>;
  @ViewChild('firstColWithIndicatorTmpl') public firstColWithIndicatorTmpl: TemplateRef<any>;

  public loading = true;
  public loadingMore = false;
  public uiReady = false;
  public footerChange: 'added' | 'removed' | null = null;
  public footerWorkNumber: string | null = null;
  public readonly scrollMinBufferPx = 400;
  public readonly scrollMaxBufferPx = 800;
  public readonly scrollItemSize = 86;
  public readonly skeletonRows = [0, 1, 2, 3, 4, 5, 6];

  public isApp: boolean;
  public currentCountryCode: 'NL' | 'DK' | 'BE' | 'DE' | 'FR' | 'LU' | 'IE' = 'NL';
  private _scannedNumber: string = '';
  private _listenToScanStartStop: any;
  private _listenToScanContent: any;

  public isSelectionLocked = (selection: AnimalSelection | null | undefined): boolean =>
    selection ? animalSelectionIsLocked(selection) : false;

  public ngOnInit(): void {
    this.isApp = this._config.source === 'app';
    this.loading = !this._hasAnimalsInStore();
    this._ensureAnimalsInStore();
  }

  private _hasAnimalsInStore(): boolean {
    const selection = this._repo.getActiveEntity();
    return Array.isArray(selection?.animals);
  }

  /** Store direct tonen (zoals gegevens-tab); alleen laden als animals nog ontbreken. */
  private _ensureAnimalsInStore(): void {
    const selection = this._repo.getActiveEntity();
    if (!selection?.id) {
      this.selection$
        .pipe(
          filter((s): s is AnimalSelection => !!s?.id),
          take(1)
        )
        .subscribe((s) => this._ensureAnimalsInStoreForSelection(s));
      return;
    }
    this._ensureAnimalsInStoreForSelection(selection);
  }

  private _ensureAnimalsInStoreForSelection(selection: AnimalSelection): void {
    if (Array.isArray(selection.animals)) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this._repo
      .getSelectionWithAnimals(selection.id, true, false)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  public ngAfterViewInit(): void {
    this.columns = this._initColumns();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.uiReady = true;
      });
    });

    if (this.isApp) {
      this._startListenToBrackets();
    }
  }

  public ngOnDestroy(): void {
    this._stopListenToContent();
    this._stopListenToBrackets();
  }

  public animalSelected(animal: Animal): void {
    this.selection$.pipe(
      take(1),
      switchMap(selection =>
        from(
          this._modalCtrl.create({
            component: AnimalSelectionAnimalDetailsDialogComponent,
            componentProps: {
              animal,
              selection: selection ? { id: selection.id } : null
            }
          })
        ).pipe(
          tap((modal) => modal.present()),
          switchMap((modal) => modal.onDidDismiss()),
          filter((res): res is { role: string } => res.role === 'cancel' || res.role === 'updated'),
          withLatestFrom(this.selection$),
          switchMap(([res, selection]: [{ role: string }, AnimalSelection]) =>
            res.role === 'cancel'
              ? this._repo.removeAnimal(selection.id, animal.id).pipe(
                  tap(() => this._setFooterChange('removed', animal.work_number)),
                  catchError(() => of(null))
                )
              : of(null)
          ),
          take(1)
        )
      )
    ).subscribe();
  }



  public addAnimal(scannedIdentificationNumber?: string): void {
    void this._openAddAnimalDialog(scannedIdentificationNumber);
  }

  private _triggerHaptic(): void {
    if (!this.isApp || !this._platform.is('capacitor')) {
      return;
    }
    void Haptics.impact({ style: ImpactStyle.Light }).catch((): void => undefined);
  }

  private async _openAddAnimalDialog(scannedIdentificationNumber?: string): Promise<void> {
    this._triggerHaptic();

    let selection = this._repo.getActiveEntity();
    if (!selection) {
      selection = await firstValueFrom(this.selection$.pipe(take(1)));
    }

    if (!selection) {
      return;
    }

    if (this.isSelectionLocked(selection)) {
      this._toast.show({
        header: 'Lijst afgesloten',
        message: 'Aan deze lijst kunnen geen dieren meer worden toegevoegd.',
        color: 'warning'
      });
      return;
    }

    const selectionContext: AnimalSelectionPoolContext = {
      id: selection.id,
      source_stable_id: selection.source_stable_id,
      min_birth_date: selection.min_birth_date,
      max_birth_date: selection.max_birth_date,
      source: selection.source,
      use_rvo: selection.use_rvo,
      species: selection.species,
    };

    const modal = await this._modalCtrl.create({
      component: AnimalSelectionAnimalDialogComponent,
      componentProps: {
        stableId: selection.source_stable_id,
        selection: selectionContext,
        scannedIdentificationNumber
      }
    });

    void modal.present();

    const { data } = await modal.onDidDismiss<AnimalSelectionAddAnimal>();
    if (!data) {
      return;
    }

    const currentSelection = await firstValueFrom(this.selection$.pipe(take(1)));

    this._repo.addAnimal(currentSelection.id, data).pipe(
      withLoading(this._loader),
      catchError((e: HttpErrorResponse) => {
        this._toast.show({
          header: 'Oeps.. dat ging niet helemaal goed...',
          message: e.error?.message ?? 'Dier toevoegen mislukt',
          color: 'danger'
        });
        return of(null);
      }),
      take(1)
    ).subscribe((response) => {
      if (!response) {
        return;
      }

      this._setFooterChange('added', response.animal?.work_number);

      if (response.rvo_error) {
        this._toast.show({
          header: 'RVO-verrijking mislukt',
          message: response.rvo_error,
          color: 'warning'
        });
      }
    });
  }

  private _initColumns(): DataTableColDef[] {
    const cols = [
      {
        headerName: this.isApp ? 'Werknr.' : 'Levensnummer',
        template: this.firstColWithIndicatorTmpl,
        valueGetter: (animal: Animal) =>
          this.isApp ? animal.work_number : (animal.country_code + animal.identification_number)
      },
      {
        headerName: 'Werknummer',
        field: 'work_number'
      },
      {
        headerName: 'Leeftijd',
        valueGetter: (animal: Animal) =>
          `${this._agePipe.transform(animal.date_of_birth, { base: 1 }) || '?'
          } dgn.`
      },
      {
        headerName: 'Geboortedatum',
        valueGetter: (animal: Animal) => this._l(animal.date_of_birth)
      },
      {
        headerName: 'Geslacht',
        template: this.genderTmpl
      },
      {
        headerName: 'Kleur',
        template: this.colorTmpl
      }
    ];

    if (this.isApp) {
      cols.splice(1, 1); // Werknummer
      cols.splice(2, 1); // Geboortedatum (index 3 na eerste splice)
    }

    return cols;
  }

  private _l = (date: string): string => {
    return this._locale.localizeDate(date, this._locale.getLocale(), {
      dateStyle: 'medium'
    });
  };

  public setFilterByMarkColorValue(value: string | null): void {
    this._filterByMarkColorValue$.next(value);
  }

  /** Alleen kleuren die voor deze selectie aanstaan (voor filterdots). */
  public getUsedColors(colors: SelectionMarkColor[] | null): SelectionMarkColor[] {
    return (colors || []).filter(c => c.used !== false);
  }

  /** Hexkleur voor selection_mark_color (filterdots en indicator). */
  public getMarkColorHex(value: string): string {
    return getSelectionMarkColorHex(value);
  }

  /** Tooltip voor kleurfilter: naam + leeftijdsband indien ingesteld. */
  public getColorFilterTitle(c: SelectionMarkColor): string {
    return getMarkColorFilterTitle(c);
  }

  /** Aantal geselecteerde dieren met deze markering (voor in de filter-dots). */
  public getSelectedCountForColor(selection: AnimalSelection | null, colorValue: string): number {
    if (!selection?.animals) return 0;
    return selection.animals.filter((a) => a.selection_mark_color === colorValue).length;
  }

  /** Totaal aantal dieren (voor bij "Geen filter"-dot). */
  public getTotalSelectedCount(selection: AnimalSelection | null): number {
    return selection?.animal_count ?? selection?.animals?.length ?? 0;
  }

  public loadMoreAnimals(event?: { target: { complete: () => void } }): void {
    if (this.loadingMore || this.loading) {
      event?.target?.complete();
      return;
    }

    this.selection$
      .pipe(
        take(1),
        switchMap((selection) => {
          if (!selection?.id || !this._repo.hasMoreSelectionAnimals(selection.id)) {
            event?.target?.complete();
            return of(null);
          }
          this.loadingMore = true;
          return this._repo.loadMoreSelectionAnimals(selection.id).pipe(
            catchError(() => of(null)),
            tap(() => {
              this.loadingMore = false;
              event?.target?.complete();
            })
          );
        }),
        take(1)
      )
      .subscribe();
  }

  public getTotalAnimalCount(selection: AnimalSelection | null): number {
    const pag = selection?.id ? this._repo.getAnimalsPagination(selection.id) : null;
    return pag?.total ?? selection?.animal_count ?? selection?.animals?.length ?? 0;
  }

  public getFooterWorkNumberText(): string | null {
    if (!this.footerChange || !this.footerWorkNumber) {
      return null;
    }

    return `Werknr.: ${this.footerWorkNumber}`;
  }

  private _setFooterChange(change: 'added' | 'removed', workNumber?: string | null): void {
    this.footerChange = change;
    this.footerWorkNumber =
      workNumber != null && String(workNumber).trim() !== '' ? String(workNumber) : null;
  }

  private _startListenToBrackets(): void {
    this._scannedNumber = '';

    this._listenToScanStartStop = this._renderer.listen('window', 'keydown', (event: KeyboardEvent) => {
      if (event.key === '{') {
        this._scannedNumber = '';
        if (!this._listenToScanContent) {
          this._startListenToContent();
        }
      }
      if (event.key === '}') {
        let idval = this._scannedNumber;
        if (this.currentCountryCode === 'NL') {
          idval = idval.trim().replace(/^0+/, '');
        }

        this.addAnimal(idval);
      }
    });
  }

  private _stopListenToBrackets(): void {
    if (this._listenToScanStartStop) {
      this._listenToScanStartStop();
      this._listenToScanStartStop = null;
    }
  }

  private _startListenToContent = () => {
    this._listenToScanContent = this._renderer.listen('window', 'keydown', (event: KeyboardEvent) => {
      if (!isNaN(parseInt(event.key))) {
        this._scannedNumber += event.key;
      }
    });
  };

  private _stopListenToContent = (): void => {
    if (this._listenToScanContent) {
      this._listenToScanContent();
      this._listenToScanContent = null;
    }
  }
}
